import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Coins, TrendingUp, Users, Activity, Lock, Eye, EyeOff, Trophy, ArrowRight, Hash } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Explorer = () => {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [privacyMode, setPrivacyMode] = useState('on');

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const [s, r, l] = await Promise.all([
        axios.get(`${API_URL}/api/tsb/explorer/stats`),
        axios.get(`${API_URL}/api/tsb/explorer/recent?limit=25`),
        axios.get(`${API_URL}/api/tsb/explorer/leaderboard?limit=10`),
      ]);
      setStats(s.data);
      setRecent(r.data?.transactions || []);
      setPrivacyMode(r.data?.privacy_mode || 'on');
      setLeaders(l.data?.holders || []);
    } catch { /* ignore */ }
  };

  const lookup = async () => {
    if (!search) return;
    setSearchError('');
    setSearchResult(null);
    try {
      const r = await axios.get(`${API_URL}/api/tsb/explorer/tx/${encodeURIComponent(search.trim())}`);
      setSearchResult(r.data);
    } catch (e) {
      setSearchError(e.response?.data?.detail || 'Transaction not found');
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
  const pct = (a, b) => b > 0 ? ((a / b) * 100).toFixed(2) : '0.00';

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="tsb-explorer-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Hash className="w-7 h-7 text-amber-400" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
                TSB Coin Public Explorer
              </h1>
              <p className="text-xs text-slate-400">Transparent closed-loop ledger • Search any Tx ID • Real-time stats</p>
            </div>
          </div>
          <span className="text-[10px] font-bold bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full flex items-center gap-1">
            {privacyMode === 'off' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            Privacy: {privacyMode === 'off' ? 'FULL DISCLOSURE' : 'MASKED'}
          </span>
        </div>

        {/* Search Bar */}
        <div className="bg-slate-800/40 border border-amber-500/30 rounded-2xl p-4 mb-6">
          <label className="text-xs text-slate-400">Search by Transaction ID</label>
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && lookup()}
              placeholder="Paste a Tx ID (e.g., 5409f5d8-b2de-4c03-a4a2-68efae6dc671)"
              data-testid="explorer-search-input"
              className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg font-mono text-sm"
            />
            <button onClick={lookup} data-testid="explorer-search-btn" className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg flex items-center gap-2">
              <Search className="w-4 h-4" /> Lookup
            </button>
          </div>
          {searchError && <div className="mt-2 text-sm text-red-300">{searchError}</div>}
          {searchResult && (
            <div className="mt-4 bg-slate-900/60 border border-emerald-500/40 rounded-xl p-4" data-testid="explorer-search-result">
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <div className="text-xs text-slate-400">Transaction ID</div>
                  <div className="font-mono text-xs text-amber-300 break-all">{searchResult.tx_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">Amount</div>
                  <div className={`font-bold text-lg ${searchResult.currency === 'TSB' ? 'text-amber-300' : 'text-emerald-300'}`}>
                    {searchResult.amount} {searchResult.currency}
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-slate-500">FROM</div>
                  <div className="text-emerald-300 font-semibold">{searchResult.from?.label}</div>
                  {searchResult.from?.id && <div className="text-slate-600 font-mono">{searchResult.from.id}</div>}
                </div>
                <div>
                  <div className="text-slate-500">TO</div>
                  <div className="text-amber-300 font-semibold">{searchResult.to?.label}</div>
                  {searchResult.to?.id && <div className="text-slate-600 font-mono">{searchResult.to.id}</div>}
                </div>
              </div>
              <div className="mt-3 flex justify-between flex-wrap gap-2 text-xs border-t border-slate-700 pt-2">
                <span><strong className="text-slate-300">Type:</strong> {searchResult.type}</span>
                <span><strong className="text-slate-300">Time:</strong> {new Date(searchResult.timestamp).toLocaleString()}</span>
              </div>
              {searchResult.description && (
                <div className="mt-2 text-xs text-slate-400 italic break-words">{searchResult.description}</div>
              )}
            </div>
          )}
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="explorer-stats">
            <StatCard icon={<Coins />} label="Total Supply" value={`${fmt(stats.total_supply / 1e9)}B`} sub="10 Billion Hard Cap" color="text-amber-300" />
            <StatCard icon={<TrendingUp />} label="Circulating" value={fmt(stats.circulating_supply)} sub={`${pct(stats.circulating_supply, stats.total_supply)}%`} color="text-emerald-300" />
            <StatCard icon={<Users />} label="Holders" value={fmt(stats.total_holders)} sub={`${fmt(stats.total_transactions)} total tx`} color="text-purple-300" />
            <StatCard icon={<Activity />} label="24h Activity" value={fmt(stats.transactions_last_24h)} sub={`${fmt(stats.total_p2p_transactions)} P2P all-time`} color="text-blue-300" />
            <StatCard icon={<Lock />} label="Govt Locked" value={`${fmt(stats.govt_reserve_locked / 1e9)}B`} sub={`Till ${stats.govt_reserve_unlock_year}`} color="text-red-300" />
            <StatCard icon={<TrendingUp />} label="TSB Volume (all-time)" value={fmt(stats.total_tsb_volume)} sub="Across all transfers" color="text-orange-300" />
            <StatCard icon={<Coins />} label="Minted" value={fmt(stats.total_minted)} sub={`${pct(stats.total_minted, stats.total_supply)}% of supply`} color="text-cyan-300" />
            <StatCard icon={<Activity />} label="Total Purchases" value={fmt(stats.total_purchases)} sub="50/50 checkout events" color="text-pink-300" />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Transactions */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Activity className="w-5 h-5 text-amber-400" /> Recent Transactions</h2>
            <div className="space-y-2" data-testid="explorer-recent-list">
              {recent.map((t) => (
                <div key={t.tx_id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 text-sm">
                  <div className="flex justify-between items-start flex-wrap gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">{t.type}</span>
                        <span className="text-[10px] text-slate-500">{new Date(t.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="mt-1 text-xs font-mono flex items-center gap-2 flex-wrap">
                        <span className="text-emerald-300">{t.from?.label}</span>
                        <ArrowRight className="w-3 h-3 text-slate-500" />
                        <span className="text-amber-300">{t.to?.label}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono mt-1 truncate">Tx: {t.tx_id}</div>
                    </div>
                    <div className={`font-bold whitespace-nowrap ${t.currency === 'TSB' ? 'text-amber-300' : 'text-emerald-300'}`}>
                      {t.amount} {t.currency}
                    </div>
                  </div>
                </div>
              ))}
              {recent.length === 0 && <div className="text-slate-500 text-sm">No transactions yet</div>}
            </div>
          </div>

          {/* Leaderboard */}
          <div>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Top Holders</h2>
            <div className="space-y-2" data-testid="explorer-leaderboard">
              {leaders.map((h) => {
                const idForLink = (h.holder?.id || '').replace('...', '').slice(0, 6);
                return (
                <Link key={h.rank} to={`/explorer/holder/${idForLink}`} className={`flex items-center gap-3 p-3 rounded-lg border hover:scale-[1.02] transition-transform ${
                  h.rank === 1 ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/10 border-amber-400' :
                  h.rank <= 3 ? 'bg-slate-800/60 border-slate-600' :
                  'bg-slate-800/40 border-slate-700'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    h.rank === 1 ? 'bg-amber-400 text-black' :
                    h.rank === 2 ? 'bg-slate-300 text-black' :
                    h.rank === 3 ? 'bg-orange-600 text-white' :
                    'bg-slate-700 text-slate-300'
                  }`}>
                    {h.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{h.holder.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">{h.holder.id}</div>
                  </div>
                  <div className="text-amber-300 font-bold text-sm whitespace-nowrap">{fmt(h.balance)} TSB</div>
                </Link>
              );
              })}
              {leaders.length === 0 && <div className="text-slate-500 text-sm">No holders yet</div>}
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-slate-500 mt-8 italic">
          🔒 TSB is a closed-loop loyalty system. Not a cryptocurrency. Privacy controlled by platform admin.
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-3">
    <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400`}>
      <span className={color}>{icon}</span> {label}
    </div>
    <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
    <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
  </div>
);

export default Explorer;
